import { AfterContentInit, Component, ContentChildren, EventEmitter, Input, OnDestroy, Output, QueryList, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FormControlEx } from '../forms/form-control';
import { TabOption } from './tab-option';
import { TabComponent } from './tab/tab.component';
import { TabEventSelectedChange } from './tab-event.selected-change';

export type DisabledTab = {
  index: number; // tab index
  helpText: string; // explains why the tab is disabled
}

@Component({
    selector: 'app-tab-group',
    templateUrl: './tab-group.component.html',
    styleUrls: ['./tab-group.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TabGroupComponent<TValue = any, TTemplate = any> implements AfterContentInit, OnDestroy {
  @ContentChildren(TabComponent) tabs: QueryList<TabComponent>;
  @Output() selectedTabChange: EventEmitter<TabEventSelectedChange<TValue>> = new EventEmitter<TabEventSelectedChange<TValue>>();
  @Input() disabled = false;
  @Input() disabledTabs?: DisabledTab[];
  @Input() control?: AbstractControl<TValue>;
  @Input() options?: TabOption<TValue, TTemplate>[];
  @Input() initialTabKey?: string;
  @Input() set selectedTabIndex(value: number) {
    if (typeof value === 'number' && value !== this.selectedTab) this.selectTab(value);
  }

  selectedTab: number = 0;
  private selectedValue?: TValue;
  tabsArray: TabComponent<TValue, TTemplate>[];
  private valueChangeSubscription?: Subscription;
  private tabsChangeSubscription: Subscription;

  get disabledIndexList() {
    return this.disabledTabs?.map(x => x.index) ?? [];
  }

 getDisabledTabTooltip(tabIndex: number): string {
    return this.disabledTabs?.find(x => x.index === tabIndex)?.helpText || null;
  }

  ngAfterContentInit() {
    this.initializeTabs();
    this.tabsChangeSubscription = this.tabs.changes.subscribe(() => {
      this.initializeTabs();
    });
  }

  selectTab(index: number): void {
    if (!this.disabled && !this.disabledTabs?.map(x => x.index).includes(index)) {
      this.tabsArray.forEach((tab, i) => (tab.active = i === index));
      const value = this.tabsArray[index].value;
      this.selectedTab = index;
      this.selectedValue = value;
      this.selectedTabChange.emit({ index, value, isClickEvent: true });

      if (this.control && this.isValidOptionsValue(value)) {
        this.control.setValue(value);
      }
    }
  }

  private initializeTabs(): void {
    const isReinitialization = this.tabsArray?.length > 0;
    this.tabsArray = this.tabs.toArray();

    if (this.tabsArray.length > 0) {
      if (this.control && this.options) {
        this.updateSelectedTab(this.control.value);

        this.valueChangeSubscription = this.control.valueChanges.subscribe(value => {
          this.updateSelectedTab(value);
        });
      } else if (!isReinitialization && this.initialTabKey) {
        this.selectTabByKey(this.initialTabKey);
      }

      setTimeout(() => {
        if (this.tabsArray.length > 0) {
          if (isReinitialization) {
            this.updateSelectedTab(this.selectedValue, true);
          }

          if (this.selectedTab < 0 || this.selectedTab >= this.tabsArray.length) {
            console.error(`Invalid tab index: ${this.selectedTab} (Is reinitialization: ${isReinitialization})`);
          }

          this.tabsArray[this.selectedTab].active = true;
        }
      });
    }
  }

  private updateSelectedTab(value: TValue, skipValidCheck?: boolean): void {
    if (skipValidCheck || this.isValidOptionsValue(value)) {
      const index = this.tabsArray.findIndex(tab => tab.value === value);
      if (index >= 0) {
        this.selectedTab = index;
        this.selectedValue = value;
        this.selectedTabChange.emit({ index, value, isClickEvent: false });
        return;
      }
    }
    console.error(`Invalid tab value: ${value}`);
  }

  private isValidOptionsValue(value: TValue): boolean {
    return this.options?.some(option => option.value === value) || false;
  }

  public selectTabByKey(tabKey: string) {
    const index = this.tabsArray?.findIndex(tab => tab.key === tabKey);
    if (index >= 0) {
      this.selectedTab = index;
    } else if (this.tabsArray?.length) {
      console.error(`Invalid tab key: '${tabKey}'`);
    } else {
      console.error(`Tab list empty or not initialized yet. (Tab key: '${tabKey}')`);
    }
  }

  ngOnDestroy(): void {
    this.valueChangeSubscription?.unsubscribe();
    this.tabsChangeSubscription?.unsubscribe();
  }
}
